import { Button } from '@heroui/react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ExportProgressToastConfig, ExportSuccessToastConfig, ExportToastProps } from '../types';

/**
 * Shows a success toast for exports
 * @param config Toast configuration
 */
export function showExportSuccessToast(config: ExportSuccessToastConfig) {
    toast.success(
        ({ closeToast: _closeToast }) => (
            <div className="flex flex-col">
                <div className="font-semibold">{config.title}</div>
                <div>{config.description}</div>
                {(config.downloadUrl || config.additionalAction) && (
                    <div className="mt-2 flex gap-x-2">
                        {config.downloadUrl && (
                            <a href={config.downloadUrl} target="_blank" rel="noopener noreferrer">
                                <Button color="success" size="sm" variant="bordered">
                                    {config.downloadButtonText || 'Download Excel'}
                                </Button>
                            </a>
                        )}
                        {config.additionalAction && (
                            <Button
                                className="underline-offset-2"
                                color="success"
                                size="sm"
                                variant="light"
                                onPress={config.additionalAction.onClick}
                            >
                                {config.additionalAction.text}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        ),
        {
            position: 'bottom-right',
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            closeButton: true,
            autoClose: false,
        },
    );
}

/**
 * Shows a progress toast for exports
 * @param config Toast configuration
 * @returns Toast ID for updates
 */
export function showExportProgressToast(config: ExportProgressToastConfig) {
    return toast.info(
        <div className="flex flex-col">
            <div className="font-semibold">{config.title || 'Export in Progress'}</div>
            <div>{config.description}</div>
        </div>,
        {
            position: 'bottom-right',
            autoClose: false,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: config.progress ? config.progress / 100 : 0,
            closeButton: true,
        },
    );
}

/**
 * Updates a progress toast for exports
 * @param toastId Toast ID to update
 * @param config Toast configuration
 */
export function updateExportProgressToast(
    toastId: string | number,
    config: ExportProgressToastConfig,
) {
    toast.update(toastId, {
        render: (
            <div className="flex flex-col">
                <div className="font-semibold">{config.title || 'Export in Progress'}</div>
                <div>{config.description}</div>
            </div>
        ),
        progress: config.progress ? config.progress / 100 : 0,
        autoClose: false,
    });
}

/**
 * Component that renders an export button and shows a toast on click
 */
export function ExportToast({
    buttonText = 'Export Document',
    onExport,
    isExporting = false,
}: ExportToastProps) {
    return (
        <Button
            variant="flat"
            isLoading={isExporting}
            onPress={() => {
                if (onExport) {
                    onExport();
                } else {
                    // Default toast if no onExport function is provided
                    toast.success(
                        ({ closeToast: _closeToast }) => (
                            <div className="flex flex-col">
                                <div className="font-semibold">Successful!</div>
                                <div>Document uploaded to cloud successfully</div>
                                <div className="mt-2 flex gap-x-2">
                                    <Button color="primary" size="sm" variant="bordered">
                                        Download Document
                                    </Button>
                                    <Button
                                        className="underline-offset-2"
                                        color="primary"
                                        size="sm"
                                        variant="light"
                                    >
                                        Send to Documents
                                    </Button>
                                </div>
                            </div>
                        ),
                        {
                            position: 'bottom-right',
                            hideProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true,
                            draggable: true,
                            closeButton: true,
                        },
                    );
                }
            }}
        >
            {buttonText}
        </Button>
    );
}

/**
 * Shows an error toast for exports
 * @param title Title of the toast
 * @param description Description of the toast
 */
export function showExportErrorToast(title: string, description: string) {
    toast.error(
        <div className="flex flex-col">
            <div className="font-semibold">{title}</div>
            <div>{description}</div>
        </div>,
        {
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            closeButton: true,
        },
    );
}

// Set display name for debugging in React DevTools
ExportToast.displayName = 'ExportToast';
